import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    ListItem,
    ListItemText,
} from "@mui/material";
import React, { useState } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "../../../../utils/axiosInstance";
import { ContentCopy, Error, Upload } from "@mui/icons-material";

const GenerateFeed = () => {
    const [mediaInfoDialogOpen, setMediaInfoDialogOpen] = useState(false);
    const [jsonData, setJsonData] = useState<Record<string, unknown>>({});
    const [errorData, setErrorData] = useState<number>(0);
    const [errorDetails, setErrorDetails] = useState<Record<string, unknown>>(
        {}
    );
    const [isLoading, setIsLoading] = useState(false);
    const [fileUrl, setFileUrl] = useState("");
    const [isUploading, setIsUploading] = useState(false);

    // Create blob from jsonData - memoize it so it's only created when jsonData changes
    const jsonBlob = React.useMemo(() => {
        if (Object.keys(jsonData).length === 0) return null;
        return new Blob([JSON.stringify(jsonData, null, 2)], {
            type: "application/json",
        });
    }, [jsonData]);

    const UploadButton = () => {
        return (
            <>
                <Button
                    disabled={isLoading || isUploading}
                    onClick={fileUrl ? onCopyToClipboard : onUploadToS3}
                    variant="outlined"
                    startIcon={
                        isUploading ? (
                            <CircularProgress size={20} />
                        ) : fileUrl ? (
                            <ContentCopy />
                        ) : (
                            <Upload />
                        )
                    }
                    sx={{ marginRight: "10px" }}
                >
                    {fileUrl ? "Copy URL" : "Upload to S3"}
                </Button>
            </>
        );
    };

    const onCopyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(fileUrl);
        } catch (error) {
            console.error("Error copying to clipboard", error);
        }
    };

    const generateSearchFeedAPI = async () => {
        try {
            setIsLoading(true);
            setMediaInfoDialogOpen(true);
            const response = await axios.get(
                `${process.env.BASE_URL}api/v1/cms/search-feed?platform=roku`
            );
            setJsonData(response.data.data.json);
            setErrorData(response.data.data.errors);
            setErrorDetails(response.data.data.details);
            setIsLoading(false);
        } catch (error) {
            console.error("Error generating search feed", error);
            setMediaInfoDialogOpen(false);
            setIsLoading(false);
        }
    };

    const closeDialog = async () => {
        setMediaInfoDialogOpen(false);
        setJsonData({});
        setErrorData(0);
        setErrorDetails({});
        setFileUrl("");
    };

    const onUploadToS3 = async () => {
        if (!jsonBlob) return;

        try {
            setIsUploading(true);

            const formData = new FormData();
            formData.append("file", jsonBlob, "roku-search-feed.json");
            formData.append("platform", "roku");

            const response = await axios.post(
                `${process.env.BASE_URL}api/v1/cms/search-feed/upload`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                }
            );

            setFileUrl(response.data.data.url);
            setIsUploading(false);
        } catch (error) {
            console.error("Error uploading to S3", error);
            setIsUploading(false);
        }
    };

    const onDownloadJSON = () => {
        if (!jsonBlob) return;

        const url = URL.createObjectURL(jsonBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `roku-search-feed.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <>
            <ListItem
                onClick={isLoading ? undefined : generateSearchFeedAPI}
                className="todo-item"
            >
                <ListItemText
                    primary={"Generate Roku Search Feed"}
                    primaryTypographyProps={{
                        fontWeight: "bold",
                        textAlign: "center",
                    }}
                />
            </ListItem>

            <Dialog
                open={mediaInfoDialogOpen}
                onClose={closeDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Roku Search Feed Generation Errors</DialogTitle>
                <DialogContent dividers>
                    <pre
                        style={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            maxHeight: "400px",
                            overflow: isLoading ? "hidden" : "auto",
                        }}
                    >
                        {Object.keys(errorDetails).length ? (
                            JSON.stringify(errorDetails, null, 2)
                        ) : isLoading ? (
                            <CircularProgress />
                        ) : (
                            "No Errors Found"
                        )}
                    </pre>
                </DialogContent>
                <DialogActions>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                            alignItems: "center",
                        }}
                    >
                        {errorData > 0 && (
                            <span style={{ color: "darkred" }}>
                                <Error style={{ verticalAlign: "middle" }} />
                                {` ${errorData} assets were skipped due to errors.`}
                            </span>
                        )}
                        <div style={{ marginLeft: "auto" }}>
                            <UploadButton />
                            <Button
                                disabled={isLoading || !jsonBlob}
                                onClick={onDownloadJSON}
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                            >
                                Download JSON
                            </Button>

                            <Button
                                disabled={isLoading}
                                onClick={closeDialog}
                                color="secondary"
                            >
                                Close
                            </Button>
                        </div>
                    </div>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default GenerateFeed;
